// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Play, RotateCcw, Square } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useAuthStore } from "~/modules/auth/auth-store";
import {
  buildDpoPanelReferenceIndex,
  buildDpoPlaybackUrlIndex,
  groupDpoRatingPanels,
  resolveDpoVideoUrl,
  type DpoRatingPanel,
} from "~/modules/dpo-feedback/panels";
import { listDpoPanelBatchesQuery } from "~/modules/dpo-feedback/queries";
import {
  dpoPanelProgressStorageKey,
  dpoVoteSessionStorageKey,
  useDpoPanelProgress,
  useDpoVoteLedger,
} from "~/modules/dpo-feedback/vote-session";
import type { DpoSynthesizedVote } from "~/modules/dpo-feedback/votes";
import { MirageEvalMetric, MirageEvalStatusBadge } from "~/modules/mirage-video-evals/components";
import {
  closeMirageVideoEvalTestMutation,
  openMirageVideoEvalTestMutation,
  submitMirageVideoEvalVoteMutation,
} from "~/modules/mirage-video-evals/mutations";
import { getMirageVideoEvalTestQuery } from "~/modules/mirage-video-evals/queries";

import { DpoPanelGrid } from "./-components/dpo-panel-grid";

export const Route = createFileRoute("/_dash/videos/evals/dpo/$testId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { testId } = Route.useParams();
  const navigate = useNavigate();
  const [panelIndex, setPanelIndex] = useState(0);
  const [sessionVotes, setSessionVotes] = useState(0);
  const [audioArmed, setAudioArmed] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const {
    data: test,
    isLoading,
    error,
  } = getMirageVideoEvalTestQuery.useQuery({
    pathParams: { testId },
  });

  const batchesQuery = listDpoPanelBatchesQuery.useQuery({
    retry: false,
    refetchOnWindowFocus: true,
  });

  const [user] = useAuthStore((store) => [store.user]);
  const voteLedger = useDpoVoteLedger(dpoVoteSessionStorageKey(testId, user?.userId));
  const progressStorageKey = dpoPanelProgressStorageKey(testId, user?.userId);
  const panelProgress = useDpoPanelProgress(progressStorageKey);

  const scopedBatches = useMemo(() => {
    const batches = batchesQuery.data?.batches ?? [];
    return test?.checkpoint
      ? batches.filter((batch) => batch.checkpointSlug === test.checkpoint)
      : batches;
  }, [batchesQuery.data, test?.checkpoint]);

  const urlIndex = useMemo(() => buildDpoPlaybackUrlIndex(scopedBatches), [scopedBatches]);
  const referenceIndex = useMemo(() => buildDpoPanelReferenceIndex(scopedBatches), [scopedBatches]);

  const panels = useMemo(() => (test ? groupDpoRatingPanels(test.videos) : []), [test]);

  // Resume: fast-forward past panels this rater already completed
  // (submitted OR skipped) in a previous session — once per PROGRESS KEY,
  // not per mount: TanStack reuses this component across $testId param
  // changes, and userId can arrive after panels (CapCode review on #4001).
  // Keying the guard also resets a stale panelIndex carried over from the
  // previous test. "Start over" is not fought: the key doesn't change.
  const fastForwardKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (fastForwardKeyRef.current === progressStorageKey || panels.length === 0) {
      return;
    }
    fastForwardKeyRef.current = progressStorageKey;
    const firstUnrated = panels.findIndex(
      (candidate) => !panelProgress.completedClipIds.has(candidate.clipId)
    );
    const resumeIndex = firstUnrated === -1 ? panels.length : firstUnrated;
    setPanelIndex(resumeIndex);
    if (resumeIndex > 0) {
      toast.info(
        firstUnrated === -1
          ? `All ${panels.length} panels already rated`
          : `Resumed at panel ${resumeIndex + 1} — ${resumeIndex} already rated`
      );
    }
  }, [panelProgress.completedClipIds, panels, progressStorageKey]);

  const panel = panels.at(panelIndex);
  const nextPanel = panels.at(panelIndex + 1);

  const submitVote = async (vote: DpoSynthesizedVote) => {
    await submitMirageVideoEvalVoteMutation.trigger({
      pathParams: { testId },
      params: vote,
    });
  };

  const handlePanelComplete = ({ submittedVotes }: { submittedVotes: number }) => {
    if (panel) {
      panelProgress.markComplete(panel.clipId);
    }
    setSessionVotes((count) => count + submittedVotes);
    setPanelIndex((index) => index + 1);
  };

  const startOver = () => {
    panelProgress.reset();
    setPanelIndex(0);
  };

  const openTest = async () => {
    setIsOpening(true);
    try {
      await openMirageVideoEvalTestMutation.trigger({ pathParams: { testId }, params: {} });
      toast.success("Eval opened");
    } catch (error) {
      toast.error("Failed to open eval", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsOpening(false);
    }
  };

  const closeTest = async () => {
    setIsClosing(true);
    try {
      await closeMirageVideoEvalTestMutation.trigger({ pathParams: { testId }, params: {} });
      toast.success("Eval closed");
      setShowCloseConfirm(false);
      await navigate({ to: "/videos/evals/dpo" });
    } catch (error) {
      toast.error("Failed to close eval", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsClosing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading test...</div>
    );
  }

  if (error || !test) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load DPO feedback test</p>
        {error && <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals/dpo">Back to DPO feedback</Link>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">{test.name}</h1>
            <MirageEvalStatusBadge status={test.status} />
          </div>
          <div className="mt-1 flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <span>{test.testId}</span>
            {panel && (
              <>
                <span>|</span>
                <span>{panel.clipId}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/evals/$testId/results" params={{ testId }}>
              Results
            </Link>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCloseConfirm(true)}
            disabled={test.status !== "open"}
          >
            <Square className="size-4" />
            Close test
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <MirageEvalMetric
          label="Panel"
          value={`${Math.min(panelIndex + 1, panels.length)} / ${panels.length}`}
          hint={
            panelProgress.completedClipIds.size > 0
              ? `${panelProgress.completedClipIds.size} rated across sessions`
              : undefined
          }
        />
        <MirageEvalMetric label="Votes (session)" value={sessionVotes} />
        <MirageEvalMetric label="Votes (total)" value={test.voteCount} />
        <MirageEvalMetric label="Status" value={test.status} />
      </div>

      {test.status === "draft" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed p-4 text-sm">
          <span className="text-muted-foreground">
            This test is still a draft. Open it to start recording votes.
          </span>
          <Button size="sm" onClick={openTest} disabled={isOpening}>
            {isOpening ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
            Open
          </Button>
        </div>
      )}

      {test.status === "closed" && (
        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          This test is closed. The grid is read-only.
        </div>
      )}

      {panels.length === 0 && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">
          No panels found in this test.
        </div>
      )}

      {panel && (
        <DpoPanelGrid
          key={panel.clipId}
          panel={panel}
          autoStartWithAudio={audioArmed}
          disabled={test.status !== "open" || showCloseConfirm}
          onAudioArmed={() => setAudioArmed(true)}
          onComplete={handlePanelComplete}
          submitVote={submitVote}
          urlIndex={urlIndex}
          reference={referenceIndex.get(panel.segmentHash)}
          voteLedger={voteLedger}
        />
      )}

      {nextPanel && (
        <div className="hidden" aria-hidden="true">
          {panelPlaybackUrls(nextPanel, urlIndex).map((url) => (
            <video key={url} src={url} preload="auto" muted playsInline />
          ))}
        </div>
      )}

      {!panel && panels.length > 0 && (
        <div className="space-y-4 rounded-md border p-6 text-center">
          <CheckCircle2 className="mx-auto size-8 text-emerald-600" />
          <div>
            <p className="font-medium">All {panels.length} panels reviewed</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {sessionVotes} {sessionVotes === 1 ? "vote" : "votes"} submitted this session.
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={startOver}>
              Start over from panel 1
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPanelIndex(0)}>
              <RotateCcw className="size-4" />
              Review again
            </Button>
            <Button
              size="sm"
              onClick={() => setShowCloseConfirm(true)}
              disabled={test.status !== "open"}
            >
              <Square className="size-4" />
              Close test
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close this DPO feedback test?</DialogTitle>
            <DialogDescription>
              Closing freezes voting for {test.name}. Recorded votes ({test.voteCount} total) are
              kept and the results become final for packaging.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCloseConfirm(false)}
              disabled={isClosing}
            >
              Cancel
            </Button>
            <Button onClick={closeTest} disabled={isClosing}>
              {isClosing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Square className="size-4" />
              )}
              Close test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function panelPlaybackUrls(panel: DpoRatingPanel, urlIndex: ReadonlyMap<string, string>): string[] {
  const videos = panel.gtVideo ? [panel.gtVideo, ...panel.seedVideos] : panel.seedVideos;
  return videos
    .map((video) => resolveDpoVideoUrl(video, urlIndex))
    .filter((url): url is string => typeof url === "string");
}
